import React, { useState } from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import TermsForm from './components/TermsForm';
import DocumentPreview from './components/DocumentPreview';
import './App.css';

const {FiFileText, FiUser, FiDownload, FiZap} = FiIcons;

function App() {
  const [formData, setFormData] = useState({
    educator_name: '',
    business_name: '',
    business_location: '',
    contact_email: '',
    booking_link: '',
    services_list: '',
    cancellation_window_hours: 24,
    reschedule_window_hours: 8,
    purchase_type: 'One-off',
    credit_expiration_months: 12,
    subscription_expiration_days: 30,
    camera_policy: 'Required',
    headphones_policy: 'Optional',
    classroom_rules: '',
    in_person_offered: 'No',
    in_person_details: '',
    last_updated_date: new Date().toISOString().split('T')[0]
  });

  const [showPreview, setShowPreview] = useState(false);

  const handleFormSubmit = (data) => {
    setFormData(data);
    setShowPreview(true);
  };

  const handleBackToForm = () => {
    setShowPreview(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://blog.super-teacher.net/wp-content/uploads/2024/01/ST-favicon-1.png" 
              alt="SuperTeacher Icon" 
              className="h-8 w-auto mr-3"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div 
              className="hidden items-center justify-center bg-indigo-100 p-2 rounded-full mr-3"
              style={{ display: 'none' }}
            >
              <SafeIcon icon={FiFileText} className="text-xl text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">
              EduTerms Generator
            </h1>
          </div>
          
          {/* SuperTeacher Logo */}
          <div className="flex items-center justify-center mb-6">
            <a 
              href="https://super-teacher.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 transition-colors group"
            >
              <img 
                src="https://blog.super-teacher.net/wp-content/uploads/2024/01/ST-favicon-1.png" 
                alt="SuperTeacher Logo" 
                className="h-6 w-auto transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div 
                className="hidden items-center space-x-2 bg-indigo-100 px-3 py-1 rounded-full"
                style={{ display: 'none' }}
              >
                <SafeIcon icon={FiFileText} className="text-lg text-indigo-600" />
                <span className="text-sm font-medium text-indigo-600">SuperTeacher</span>
              </div>
              <span className="text-sm font-medium">super-teacher.net</span>
            </a>
          </div>
          
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Create personalized Terms & Conditions for your educational services. 
            Fill out the form below and generate a professional document ready for download.
          </p>
          
          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-green-500 to-blue-600 rounded-xl p-6 max-w-2xl mx-auto shadow-xl"
          >
            <div className="flex items-center justify-center mb-3">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-full mr-3">
                <SafeIcon icon={FiZap} className="text-xl text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                Perfect for Independent Educators
              </h2>
            </div>
            <p className="text-white/90 text-lg mb-4">
              Make your classroom policies setup a breeze! Generate professional, legally-sound Terms & Conditions in minutes - not hours.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-white font-medium">⚡ Quick Setup</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-white font-medium">📋 Pre-Built Policies</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-white font-medium">💼 Ready to Use</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${!showPreview ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!showPreview ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                <SafeIcon icon={FiUser} className="text-sm" />
              </div>
              <span className="ml-2 font-medium">Form Details</span>
            </div>
            <div className="w-8 h-px bg-gray-300"></div>
            <div className={`flex items-center ${showPreview ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${showPreview ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                <SafeIcon icon={FiDownload} className="text-sm" />
              </div>
              <span className="ml-2 font-medium">Preview & Download</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <motion.div
          key={showPreview ? 'preview' : 'form'}
          initial={{ opacity: 0, x: showPreview ? 50 : -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: showPreview ? -50 : 50 }}
          transition={{ duration: 0.3 }}
        >
          {!showPreview ? (
            <TermsForm 
              initialData={formData}
              onSubmit={handleFormSubmit}
            />
          ) : (
            <DocumentPreview 
              data={formData}
              onBack={handleBackToForm}
            />
          )}
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12 pt-8 border-t border-gray-200"
        >
          <p className="text-gray-600 text-sm">
            Built with ❤️ by{' '}
            <a 
              href="https://super-teacher.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
            >
              SuperTeacher
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default App;